import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Hammer } from 'lucide-react';

import type {
  LitSquadCraftInput,
  LitSquadCraftIngredient,
  LitSquadCraftSlot,
  LitSquadCraftSlotName,
} from '@/lib/api';
import { Button } from '@/components/ui/button';

import { ArcheHeader } from '../../../components/ArcheHeader';
import { CraftIngredientSelect } from '../components/CraftIngredientSelect';
import { CraftOutcomeCard } from '../components/CraftOutcomeCard';
import { CraftResultDialog } from '../components/CraftResultDialog';
import { useLitSquadCraftIngredients } from '../hooks/useLitSquadCraftIngredients';
import { useLitSquadCraftMutation } from '../hooks/useLitSquadCraftMutation';
import { useLitSquadCraftPreview } from '../hooks/useLitSquadCraftPreview';

const EMPTY_INPUT: LitSquadCraftInput = {
  ficiant: '',
  classCoin: '',
  lituCloth: '',
  mmc: '',
  expertise: '',
  themeCoin: '',
};

const REQUIRED_SLOTS: LitSquadCraftSlotName[] = ['ficiant', 'classCoin', 'lituCloth'];
const OPTIONAL_SLOTS: LitSquadCraftSlotName[] = ['mmc', 'expertise', 'themeCoin'];

const SLOT_LABELS: Record<LitSquadCraftSlotName, string> = {
  ficiant: 'Ficiant',
  classCoin: 'Class coin',
  lituCloth: 'Litu cloth',
  mmc: 'Magical modifier (MMC)',
  expertise: 'Expertise',
  themeCoin: 'Theme coin',
};

function normalizedInput(input: LitSquadCraftInput): LitSquadCraftInput {
  return {
    ficiant: input.ficiant,
    classCoin: input.classCoin,
    lituCloth: input.lituCloth,
    mmc: input.mmc || undefined,
    expertise: input.expertise || undefined,
    themeCoin: input.themeCoin || undefined,
  };
}

function hasAllRequired(input: LitSquadCraftInput): boolean {
  return !!input.ficiant && !!input.classCoin && !!input.lituCloth;
}

function slotHasSufficientStock(
  slot: LitSquadCraftSlot | undefined,
  selectedName: string,
): boolean {
  if (!slot || !selectedName) return false;
  const ing = slot.eligibleIngredients.find((i) => i.name === selectedName);
  return !!ing && ing.userCount >= slot.quantity;
}

export function CraftPage() {
  const {
    slots,
    loading: slotsLoading,
    error: slotsError,
    refresh: refreshSlots,
  } = useLitSquadCraftIngredients();
  const preview = useLitSquadCraftPreview();
  const craft = useLitSquadCraftMutation();

  const [input, setInput] = useState<LitSquadCraftInput>(EMPTY_INPUT);
  const [showOptional, setShowOptional] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const slotMap = useMemo(() => {
    const m = new Map<LitSquadCraftSlotName, LitSquadCraftSlot>();
    for (const s of slots) m.set(s.slot, s);
    return m;
  }, [slots]);

  const requiredSufficient =
    slotHasSufficientStock(slotMap.get('ficiant'), input.ficiant) &&
    slotHasSufficientStock(slotMap.get('classCoin'), input.classCoin) &&
    slotHasSufficientStock(slotMap.get('lituCloth'), input.lituCloth);

  // For optional slots, "sufficient" only matters if the user actually
  // selected something. An unselected optional doesn't block craft.
  const optionalOk = (['mmc', 'expertise', 'themeCoin'] as const).every((slot) => {
    const selected = input[slot];
    if (!selected) return true;
    return slotHasSufficientStock(slotMap.get(slot), selected);
  });

  const canPreview = hasAllRequired(input);
  const canCraft = canPreview && requiredSufficient && optionalOk && !craft.loading;

  const updateSlot = (slot: LitSquadCraftSlotName, value: string) => {
    setInput((prev) => ({ ...prev, [slot]: value }));
    // Invalidate the current preview whenever inputs change so the user
    // re-previews before crafting.
    preview.reset();
  };

  const runPreview = async () => {
    if (!canPreview) return;
    try {
      await preview.run(normalizedInput(input));
    } catch {
      // preview.error will surface the message
    }
  };

  const runCraft = async () => {
    if (!canCraft) return;
    try {
      await craft.craft(normalizedInput(input));
      setDialogOpen(true);
      refreshSlots();
      preview.reset();
    } catch {
      // craft.error will surface the message
    }
  };

  const emptyIngredients: LitSquadCraftIngredient[] = [];

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_litsquad" title="Craft LitSquad Accessory">
        <Link to="/a/arche_litsquad">
          <Button variant="outline" size="sm">
            Back to inventory
          </Button>
        </Link>
      </ArcheHeader>
      <div className="max-w-6xl mx-auto px-4 pb-16 w-full">
        <p className="text-muted-foreground text-sm mt-4 mb-6">
          Combine DFE ingredients to mint a new LitSquad accessory NFT.
        </p>

        {slotsError && (
          <div className="border border-destructive rounded-md p-3 text-sm text-destructive mb-4">
            {slotsError}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Slot selectors */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="text-lg font-semibold">Ingredients</h2>
            {REQUIRED_SLOTS.map((slotName) => (
              <CraftIngredientSelect
                key={slotName}
                label={SLOT_LABELS[slotName]}
                required
                quantity={slotMap.get(slotName)?.quantity ?? 1}
                value={input[slotName] ?? ''}
                onChange={(v) => updateSlot(slotName, v)}
                ingredients={slotMap.get(slotName)?.eligibleIngredients ?? emptyIngredients}
                disabled={slotsLoading}
              />
            ))}

            <button
              type="button"
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setShowOptional((v) => !v)}
            >
              {showOptional ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Optional modifiers
            </button>

            {showOptional &&
              OPTIONAL_SLOTS.map((slotName) => (
                <CraftIngredientSelect
                  key={slotName}
                  label={SLOT_LABELS[slotName]}
                  required={false}
                  quantity={slotMap.get(slotName)?.quantity ?? 1}
                  value={input[slotName] ?? ''}
                  onChange={(v) => updateSlot(slotName, v)}
                  ingredients={slotMap.get(slotName)?.eligibleIngredients ?? emptyIngredients}
                  disabled={slotsLoading}
                />
              ))}

            <div className="flex flex-col gap-2 pt-2">
              <Button
                variant="outline"
                onClick={runPreview}
                disabled={!canPreview || preview.loading}
                type="button"
              >
                {preview.loading ? 'Previewing…' : 'Preview outcomes'}
              </Button>
              <Button
                onClick={runCraft}
                disabled={!canCraft || preview.preview === null}
                type="button"
              >
                <Hammer className="h-4 w-4 mr-2" />
                {craft.loading ? 'Crafting…' : 'Craft'}
              </Button>
            </div>
            {preview.error && <p className="text-sm text-destructive">{preview.error}</p>}
            {craft.error && <p className="text-sm text-destructive">{craft.error}</p>}
            {!requiredSufficient && hasAllRequired(input) && (
              <p className="text-xs text-destructive">
                You don't have enough of at least one required ingredient.
              </p>
            )}
          </div>

          {/* Preview panel */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold mb-3">Possible outcomes</h2>
            {preview.preview === null && (
              <p className="text-sm text-muted-foreground">
                Select your ingredients and press <em>Preview outcomes</em> to see what you might
                craft.
              </p>
            )}
            {preview.preview && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>
                    Species: <span className="font-medium">{preview.preview.species}</span>
                  </span>
                  <span>•</span>
                  <span>
                    Ficiant level{' '}
                    <span className="font-medium">{preview.preview.ficiantLevel}</span>
                  </span>
                  {preview.preview.useBetterOdds && (
                    <>
                      <span>•</span>
                      <span className="text-green-600 dark:text-green-400">Better odds active</span>
                    </>
                  )}
                  <span>•</span>
                  <span>Groups: {preview.preview.possibleGroups.join(', ') || '—'}</span>
                </div>

                {preview.preview.failureProbability > 0.001 && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                    <strong>
                      {(preview.preview.failureProbability * 100).toFixed(1)}% chance of no-match
                      roll.
                    </strong>{' '}
                    This recipe's possible groups don't cover every rarity tier in the catalog. If
                    the rarity roll lands on an empty tier, the craft aborts without consuming
                    ingredients — you can retry. Use a different ficiant or litu cloth to cover more
                    tiers.
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {preview.preview.possibleOutcomes.map((o) => (
                    <CraftOutcomeCard key={o.identifier} outcome={o} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <CraftResultDialog
          result={craft.result}
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            craft.reset();
            setInput(EMPTY_INPUT);
          }}
        />
      </div>
    </div>
  );
}
